import { Autocomplete, TextField, Grid } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { Merchant } from "../../common/types";
import { fetchMerchants } from "../../common/makeAPICalls";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

// Interface pour les props du composant
interface SelectMarchandProps {
  selectedMerchant?: Merchant | null;
  onMerchantSelect: (merchant: Merchant | null) => void;
}

const SelectMarchand: React.FC<SelectMarchandProps> = ({ 
  selectedMerchant: propSelectedMerchant = null,
  onMerchantSelect 
}) => {
  const { t } = useTranslation();
  
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(propSelectedMerchant);
  const [merchantsList, setMerchantsList] = useState<Merchant[]>([]);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [loading, setLoading] = useState(false);

  // Synchroniser l'état local avec les props
  useEffect(() => {
    setSelectedMerchant(propSelectedMerchant);
  }, [propSelectedMerchant]);

  // Fonction pour récupérer les marchands avec gestion du loading
  const handleFetchMerchants = useCallback(async (searchTerm: string = "") => {
    try {
      setLoading(true);
      const merchantData = await fetchMerchants(searchTerm);
      setMerchantsList(merchantData);
      setFilteredMerchants(merchantData);
    } catch (error) {
      toast.error(t("selectMerchant.errorFetchingMerchants"));
      console.error('fetchMerchants failed:', error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Fonction pour filtrer les marchands localement
  const filterMerchantsLocally = useCallback((inputValue: string) => {
    if (!inputValue.trim()) {
      setFilteredMerchants(merchantsList);
      return;
    }
    
    const filtered = merchantsList.filter((merchant) =>
      merchant.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      (merchant.email && merchant.email.toLowerCase().includes(inputValue.toLowerCase()))
    );
    setFilteredMerchants(filtered);
  }, [merchantsList]);

  // Gestion du changement d'input avec debounce
  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    setSearchValue(newInputValue);
  };

  // Effet pour gérer la recherche avec debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue.length >= 2) {
        // Faire un appel API si on a au moins 2 caractères
        handleFetchMerchants(searchValue);
      } else if (searchValue.length === 0) {
        // Si le champ est vide, charger tous les marchands ou filtrer localement
        if (merchantsList.length === 0) {
          handleFetchMerchants();
        } else {
          setFilteredMerchants(merchantsList);
        }
      } else {
        // Filtrer localement pour moins de 2 caractères
        filterMerchantsLocally(searchValue);
      }
    }, 300); // Attendre 300ms après que l'utilisateur arrête de taper

    return () => clearTimeout(timeoutId);
  }, [searchValue, handleFetchMerchants, filterMerchantsLocally, merchantsList.length]);

  // Charger les marchands au montage du composant
  useEffect(() => {
    if (merchantsList.length === 0) {
      handleFetchMerchants();
    }
  }, [handleFetchMerchants, merchantsList.length]);

  const handleMerchantSelect = (
    _event: React.SyntheticEvent,
    value: { id: string; label: string; merchant: Merchant } | null
  ) => {
    let selected: Merchant | null = null;

    if (value) {
      selected = merchantsList.find(merchant => merchant.id === value.id) || null;
    }
    
    // Mettre à jour l'état local
    setSelectedMerchant(selected);
    
    // Communiquer avec le composant parent
    onMerchantSelect(selected);
  };

  // Créer les options pour l'Autocomplete
  const autocompleteOptions = filteredMerchants.map((merchant: Merchant) => ({
    id: merchant.id,
    label: merchant.name,
    merchant: merchant // Garder une référence au marchand complet
  }));

  // Trouver la valeur sélectionnée pour l'Autocomplete
  const selectedValue = selectedMerchant 
    ? autocompleteOptions.find(option => option.id === selectedMerchant.id) || null
    : null;

  return (
    <div style={{ width: "100%" }}>
      <div>
        <div style={{ padding: "10px", display: "flex", flexDirection: "column", gap: 10 }}>
            <Autocomplete
              size="small"
              options={autocompleteOptions}
              sx={{ width: '100%' }}
              onInputChange={handleInputChange}
              onChange={handleMerchantSelect}
              inputValue={searchValue}
              value={selectedValue}
              loading={loading}
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t("selectMerchant.merchantLabel")}
                  placeholder={t("selectMerchant.searchPlaceholder")}
                />
              )}
              noOptionsText={
                searchValue.length < 2 
                  ? t("selectMerchant.minCharactersMessage")
                  : t("selectMerchant.noOptionsText")
              }
              loadingText={t("selectMerchant.loadingText")}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <strong>{option.label}</strong>
                    {option.merchant.email && (
                      <small style={{ color: '#666' }}>{option.merchant.email}</small>
                    )}
                  </div>
                </li>
              )}
            />
            {selectedMerchant && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                flexDirection: 'column', 
                outline: '1px solid #f8f8f8', 
                padding: '10px', 
                borderRadius: 2.5 
              }}>
                <h4 style={{
                  width: '100%', 
                  backgroundColor: '#f8f8f8', 
                  padding: '10px', 
                  borderRadius: 2.5, 
                  fontSize: '14px',
                  margin: '0 0 10px 0'
                }}>
                  {t("selectMerchant.companyDetails")}
                </h4>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                        {t("selectMerchant.companyName")}
                      </label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        {selectedMerchant.name}
                      </p>
                    </div>
                  </Grid>
                  <Grid size={6}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                        {t("selectMerchant.phone")}
                      </label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        {selectedMerchant.phone || t("selectMerchant.notDefined")}
                      </p>
                    </div>
                  </Grid>
                  <Grid size={6}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                        {t("selectMerchant.email")}
                      </label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        {selectedMerchant.email || t("selectMerchant.notDefined")}
                      </p>
                    </div>
                  </Grid>
                  <Grid size={6}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                        {t("selectMerchant.city")}
                      </label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        {selectedMerchant.city || t("selectMerchant.notDefined")}
                      </p>
                    </div>
                  </Grid>
                </Grid>
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default SelectMarchand;